import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  BadgePercent,
  Check,
  Gift,
  Plus,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import { Combobox } from '@/shared/components/ui/combobox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { Input } from '@/shared/components/ui/input';
import { ErrorDialog, SuccessDialog } from '@/shared/components/ui/status-dialog';
import { cn } from '@/shared/lib/cn';
import { formatIvaRate } from '@/shared/countries';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { ProcesandoOverlay } from './components/ProcesandoOverlay';
import { MoneyCell, type Moneda } from './components/MoneyCell';
import { InfoField } from './components/InfoField';
import {
  DescuentoAprobacionDialog,
  type AuthDialogState,
} from './components/DescuentoAprobacionDialog';
import { DescuentoCell } from './components/DescuentoCell';
import {
  GarantiaBadge,
  GarantiaFechaDialog,
  GarantiaToggle,
  mesesGarantiaDe,
  SIN_GARANTIA,
  type GarantiaLinea,
} from './components/Garantia';
import { FechaIngresoPicker, PorIngresarBadge, useFechasPorIngresar } from './components/FechaIngreso';
import {
  CLASE_ITEM_POR_INGRESAR,
  CLASE_ITEM_SIN_STOCK,
  estadoSiguienteUnidad,
  estadosPorLinea,
} from './lib/porIngresar';
import { useSession } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import {
  listActiveCotizableArticulos,
  articuloLlevaNroSerie,
  type ArticuloItem,
} from '@/shared/api/controllers/articulos';
import {
  listActiveContactos,
  type ContactoItem,
} from '@/shared/api/controllers/contactos';
import {
  calcStockCotizable,
  calcStockDisponible,
  listActiveInventario,
  type InventarioItem,
} from '@/shared/api/controllers/inventario';
import { listDescuentosByVendedorMes } from '@/shared/api/controllers/descuentos';
import { listAllParametrosComDesc } from '@/shared/api/controllers/parametrosComDesc';
import {
  isCampaniaVigente,
  listActiveCampanias,
  storedDecimalToPercent,
} from '@/shared/api/controllers/campanias';
import { createVenta, getRegalosMes, type VentaLinePayload } from '@/shared/api/controllers/ventas';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { calcInvoiceTotals, calcLineTotals, type IvaRate } from './lib/calc';
import {
  conIvaSegunCliente,
  esClienteExterior,
  ivaAEnviar,
  NOTA_EXPORTACION_SIN_IVA,
} from './lib/ivaExterior';
import {
  countMonthlyDescuentos,
  evaluateLineDiscount,
  readAllowanceLimits,
  readBracketPercents,
  readRegaloMensualLimit,
} from './lib/discounts';
import { isArticuloRegalable, useRegaloMinimoThreshold } from './lib/useRegaloMinimo';
import { staleConfigError, staleConfigMessage } from './lib/submitErrors';

/**
 * Nueva Cotización — Stage 2 port of `Screen_NuevaCotizacion.pa.yaml`.
 *
 * Layout mirrors the PowerApps screen exactly (see screenshot):
 *  - Top toolbar: back, title, today's date, contact picker, filter (stub).
 *  - Contact info strip with article-picker toolbar on the right.
 *  - Line-items table (or empty-state card).
 *  - Bottom totals bar with "Realizar cotización" submit.
 *
 * Moneda is inherited from the Ventas gallery toggle via `?moneda=ARS|USD`
 * query param. IVA on each line is read from `Articulo.IVA_ART`
 * (`"21"`, `"10,5"`, blank → 0%). Descuento defaults to 0 and is
 * user-editable inline; 5/10/15% trigger a monthly-allowance check,
 * anything else is an immediate "Descuento Particular" authorization.
 * Stock is loaded up-front and warned inline; `Realizar cotización`
 * disables when any line exceeds stock.
 */

type LineDraft = GarantiaLinea & {
  /** Stable key for React (not persisted). */
  key: string;
  articuloId: string;
  articulo: string;
  monto: number;
  cantidad: number;
  iva: IvaRate;
  descuento: number;
  esRegalo: boolean;
  stockDisponible: number;
  /** yyyy-mm-dd. Ingreso estimado elegido si la línea depende de stock por ingresar. */
  fechaEstimadaIngreso: string | null;
  /**
   * Serialized article (`LlevaNroSerie_ART='SI'`). Serialized lines are
   * locked to cantidad 1 — `addLine` auto-splits a cantidad>1 add into N
   * separate qty-1 lines so each unit gets its own NroSerie at despacho.
   */
  llevaNroSerie: boolean;
  /** Active campaign pct for this article (null = no campaign). */
  campaignPct: number | null;
  /** User-entered reason required when the line flips to Aprobación Requerida. */
  motivo: string;
};


function pad(n: number) {
  return String(n).padStart(2, '0');
}
function todayAr(): string {
  const d = new Date();
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}
function currentMesAnio(): string {
  const d = new Date();
  return `${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function parseIvaFromArticulo(iva: string | null | undefined): IvaRate {
  // Country-agnostic — accepts whatever rate the article carries.
  // 0 / unparseable → IVA Exento.
  const v = parseMoney(iva);
  return v != null && v > 0 ? v : 0;
}

function articuloPrice(a: ArticuloItem, moneda: Moneda): number {
  const raw = moneda === 'USD' ? a.fields.ValorUnDolar_ART : a.fields.ValorUn_ART;
  return parseMoney(raw) ?? 0;
}

// QA #2: sort key for the contactos dropdown.
// Sort by what's actually shown in the list (NombreApellido_C, with
// RazonSocial as the empresa fallback). An earlier version tried to
// pivot on Apellido_C first, but real data has too many empty/junk
// apellidos ("." sentinels, single-name personas, empresas) that
// produced inconsistent ordering at the top of the list — entries
// with an apellido sorted under their surname while apellido-less
// entries clustered under their firstname, mixing weirdly. Sorting
// by displayed label gives a strict A→Z that matches user intuition.
function contactSortKey(c: ContactoItem): string {
  const display = c.fields.NombreApellido_C?.trim() || '';
  if (display) return display.toLowerCase();
  return (c.fields.RazonSocial_C ?? '').trim().toLowerCase();
}

export function NuevaCotizacionPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { config: activeCountry } = useActiveCountry();
  const primaryCurrency = activeCountry.currency.primary as Moneda;
  // URL is the source of truth (the Ventas toolbar passes the toggle
  // through), but we accept MXN now too and fall back to the active
  // country's primary instead of always ARS — otherwise an MX user
  // landing here without ?moneda gets a hidden ARS cotización.
  const monedaParam = params.get('moneda');
  const moneda: Moneda =
    monedaParam === 'USD' || monedaParam === 'MXN' || monedaParam === 'ARS'
      ? monedaParam
      : primaryCurrency;
  const { profile } = useSession();
  const qc = useQueryClient();

  // --- data loads ---
  // Country code is in every queryKey so AR/MX caches don't collide
  // when a cross-country admin switches scope. `resetQueries()` in the
  // country-switch handler is the safety net; this is the primary
  // guarantee.
  const countryCode = activeCountry.code;
  const contactosQ = useQuery({
    queryKey: ['nueva-cotizacion', countryCode, 'contactos'],
    queryFn: listActiveContactos,
    staleTime: 60_000,
  });
  const articulosQ = useQuery({
    queryKey: ['nueva-cotizacion', countryCode, 'articulos'],
    queryFn: listActiveCotizableArticulos,
    staleTime: 60_000,
  });
  const inventarioQ = useQuery({
    queryKey: ['nueva-cotizacion', countryCode, 'inventario'],
    queryFn: listActiveInventario,
    staleTime: 60_000,
    // Stock changes in the Inventario/Artículos module, which never
    // invalidates this page-scoped key. Refetch on every open so the
    // picker's "sin stock" reflects the latest stock without a logout
    // (before, the 60s staleTime served a stale snapshot).
    refetchOnMount: 'always',
  });
  const fechasPorIngresarQ = useFechasPorIngresar();
  const descuentosMesQ = useQuery({
    queryKey: ['nueva-cotizacion', countryCode, 'descuentos-mes', profile?.usuario_app, currentMesAnio()],
    queryFn: () =>
      profile?.usuario_app
        ? listDescuentosByVendedorMes(profile.usuario_app, currentMesAnio())
        : Promise.resolve([]),
    enabled: Boolean(profile?.usuario_app),
    staleTime: 60_000,
  });
  const regalosMesQ = useQuery({
    queryKey: ['nueva-cotizacion', countryCode, 'regalos-mes', profile?.usuario_app, currentMesAnio()],
    queryFn: () =>
      profile?.usuario_app
        ? getRegalosMes(profile.usuario_app, currentMesAnio())
        : Promise.resolve(0),
    enabled: Boolean(profile?.usuario_app),
    staleTime: 60_000,
  });
  const parametrosQ = useQuery({
    queryKey: ['nueva-cotizacion', 'parametros'],
    queryFn: listAllParametrosComDesc,
    staleTime: 5 * 60_000,
  });
  const campaniasQ = useQuery({
    queryKey: ['nueva-cotizacion', countryCode, 'campanias'],
    queryFn: listActiveCampanias,
    staleTime: 5 * 60_000,
  });

  const campaignByArticulo = useMemo(() => {
    const m = new Map<string, { pct: number; nombre: string }>();
    for (const c of campaniasQ.data ?? []) {
      // Skip campaigns outside their date window — 'Activo' status alone
      // isn't enough (expiry is only nightly; starts aren't enforced).
      if (!isCampaniaVigente(c.fields)) continue;
      const aid = c.fields.ArticuloID_C;
      const pct = storedDecimalToPercent(c.fields.Descuento_C);
      if (aid && pct != null) {
        // Deterministic on overlap: if an article is in more than one
        // vigente campaign, keep the highest discount (best for the buyer)
        // instead of letting iteration order decide.
        const prev = m.get(aid);
        if (!prev || pct > prev.pct) {
          m.set(aid, { pct, nombre: c.fields.NombreCampania_C?.trim() || 'Campaña' });
        }
      }
    }
    return m;
  }, [campaniasQ.data]);

  const inventarioByArticulo = useMemo(() => {
    const m = new Map<string, InventarioItem>();
    for (const inv of inventarioQ.data ?? []) {
      const aid = inv.fields.IDArticulo_I;
      if (aid) m.set(aid, inv);
    }
    return m;
  }, [inventarioQ.data]);

  // Disponible vs. Por Ingresar de un artículo (el cotizable es la suma).
  const stockDe = (articuloId: string) => {
    const inv = inventarioByArticulo.get(articuloId);
    const disponible = Math.max(0, calcStockDisponible(inv));
    const porIngresar = Math.max(0, calcStockCotizable(inv) - disponible);
    return { disponible, porIngresar };
  };

  const limits = useMemo(() => readAllowanceLimits(parametrosQ.data ?? []), [parametrosQ.data]);
  const bracketPercents = useMemo(
    () => readBracketPercents(parametrosQ.data ?? []),
    [parametrosQ.data],
  );
  const regaloLimit = useMemo(
    () => readRegaloMensualLimit(parametrosQ.data ?? []),
    [parametrosQ.data],
  );
  const monthlyCounts = useMemo(
    () => countMonthlyDescuentos(descuentosMesQ.data ?? []),
    [descuentosMesQ.data],
  );

  // Contactos sorted alphabetically by what we show in the dropdown
  // (NombreApellido_C falls back to RazonSocial_C for empresas).
  // QA #2: keep one consistent A→Z order across the picker.
  const sortedContactos = useMemo(() => {
    return [...(contactosQ.data ?? [])].sort((a, b) =>
      contactSortKey(a).localeCompare(contactSortKey(b), 'es', { sensitivity: 'base' }),
    );
  }, [contactosQ.data]);

  // --- page state ---
  const [contact, setContact] = useState<ContactoItem | null>(null);
  const [articuloDraft, setArticuloDraft] = useState<ArticuloItem | null>(null);
  // Cantidad input state is a STRING so users can backspace the seeded
  // "1" and type a fresh value (otherwise the controlled-number pattern
  // would snap empty → 0 → 1 on every keystroke). `cantidadDraftNum`
  // below is the validated numeric the rest of the page reads.
  const [cantidadDraft, setCantidadDraft] = useState<string>('1');
  const cantidadDraftNum = (() => {
    const n = parseInt(cantidadDraft, 10);
    return Number.isFinite(n) && n >= 1 ? n : 0;
  })();
  const [giftArmed, setGiftArmed] = useState(false);
  const [garantiaArmed, setGarantiaArmed] = useState(false);
  /** Artículo sin duración de garantía esperando la fecha manual. */
  const [garantiaPendiente, setGarantiaPendiente] = useState<string | null>(null);
  const [draftLines, setLines] = useState<LineDraft[]>([]);
  // Cliente del exterior (Factura E): todas las líneas a 0% de IVA. `iva` del
  // borrador conserva la alícuota del artículo; acá sólo se aplica según el
  // cliente elegido, así al cambiarlo reaparece el IVA del artículo.
  const exterior = esClienteExterior(contact?.fields.CP_C);
  const lines = useMemo(() => conIvaSegunCliente(draftLines, exterior), [draftLines, exterior]);
  const [authDialog, setAuthDialog] = useState<AuthDialogState | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [status, setStatus] = useState<
    { kind: 'success' | 'error'; message: string } | null
  >(null);

  // Sum of cantidades already committed in the draft, grouped by
  // article. Lets the toolbar's stock check subtract them from the
  // global Disponible+PorIngresar pool so two consecutive lines on
  // the same article can't collectively oversell the stock.
  const usedByArticulo = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of lines) {
      m.set(l.articuloId, (m.get(l.articuloId) ?? 0) + l.cantidad);
    }
    return m;
  }, [lines]);

  // QA #3: article picker — show in-stock items first (A→Z), push
  // out-of-stock to the bottom. Available = global cotizable pool minus
  // anything already drafted in this cotización.
  const articleStockById = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of articulosQ.data ?? []) {
      const total = calcStockCotizable(inventarioByArticulo.get(a.id));
      const used = usedByArticulo.get(a.id) ?? 0;
      m.set(a.id, Math.max(0, total - used));
    }
    return m;
  }, [articulosQ.data, inventarioByArticulo, usedByArticulo]);

  const sortedArticulos = useMemo(() => {
    const items = [...(articulosQ.data ?? [])];
    return items.sort((a, b) => {
      const sa = (articleStockById.get(a.id) ?? 0) > 0 ? 0 : 1;
      const sb = (articleStockById.get(b.id) ?? 0) > 0 ? 0 : 1;
      if (sa !== sb) return sa - sb;
      const la = (a.fields.Articulo_ART ?? a.fields.Concat_ART ?? '').trim();
      const lb = (b.fields.Articulo_ART ?? b.fields.Concat_ART ?? '').trim();
      return la.localeCompare(lb, 'es', { sensitivity: 'base' });
    });
  }, [articulosQ.data, articleStockById]);

  // --- line manipulation ---
  /** `venceManual`: vencimiento puesto a mano cuando el producto no tiene duración de garantía. */
  const addLine = (venceManual?: string) => {
    if (!articuloDraft) return;
    if (cantidadDraftNum <= 0) return;
    const mesesGarantia = mesesGarantiaDe(articuloDraft.fields.GarantiaMeses_ART);
    if (garantiaArmed && mesesGarantia == null && !venceManual) {
      setGarantiaPendiente(articuloDraft.fields.Articulo_ART?.trim() || 'Artículo');
      return;
    }
    const garantia: GarantiaLinea = garantiaArmed
      ? { llevaGarantia: true, garantiaMeses: mesesGarantia, garantiaVence: mesesGarantia == null ? venceManual! : null }
      : SIN_GARANTIA;
    const inv = inventarioByArticulo.get(articuloDraft.id);
    const total = calcStockCotizable(inv);
    const used = usedByArticulo.get(articuloDraft.id) ?? 0;
    const remaining = Math.max(0, total - used);
    if (cantidadDraftNum > remaining) return;
    const campaign = campaignByArticulo.get(articuloDraft.id) ?? null;
    // PowerApps pre-fills the campaign discount on add (Screen_NuevaCotizacion
    // line 1109). Regalo still overrides to 100% — but only accessories
    // (Cuenta Contable = Accesorios) may be gifted, so guard here too in
    // case `giftArmed` lingered from a different article selection.
    const applyGift =
      giftArmed && isArticuloRegalable(articuloDraft) && monthlyGiftOk;
    const initialDescuento = applyGift ? 100 : campaign?.pct ?? 0;
    // Serialized articles (hornos, etc.) are one unit per line so each
    // physical unit gets its own NroSerie at despacho — split a cantidad>1
    // add into N qty-1 lines instead of a single multi-unit line.
    const llevaNroSerie = articuloLlevaNroSerie(articuloDraft);
    const lineCount = llevaNroSerie ? cantidadDraftNum : 1;
    const perLineCantidad = llevaNroSerie ? 1 : cantidadDraftNum;
    const baseKey = `${articuloDraft.id}-${Date.now()}`;
    const newLines: LineDraft[] = Array.from({ length: lineCount }, (_, i) => ({
      key: lineCount > 1 ? `${baseKey}-${i}` : baseKey,
      articuloId: articuloDraft.id,
      articulo: articuloDraft.fields.Articulo_ART?.trim() || articuloDraft.fields.Concat_ART || '—',
      monto: articuloPrice(articuloDraft, moneda),
      cantidad: perLineCantidad,
      iva: parseIvaFromArticulo(articuloDraft.fields.IVA_ART),
      descuento: initialDescuento,
      esRegalo: applyGift,
      // We store the global cotizable total per article (Disp +
      // PorIng); the per-line "exceeds stock?" check uses the
      // aggregated `usedByArticulo` to decide.
      stockDisponible: total,
      fechaEstimadaIngreso: null,
      llevaNroSerie,
      campaignPct: campaign?.pct ?? null,
      motivo: '',
      ...garantia,
    }));
    setLines((prev) => [...prev, ...newLines]);
    setArticuloDraft(null);
    setCantidadDraft('1');
    setGiftArmed(false);
    setGarantiaArmed(false);
  };

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const removeLine = (key: string) => {
    setLines((prev) => prev.filter((l) => l.key !== key));
  };

  /**
   * Fired on blur of the descuento input: if the committed value needs
   * authorization, open the motivo dialog. If it doesn't, clear any stale
   * motivo from a prior auth-required value on the same line.
   */
  const handleDescuentoCommit = (key: string, previousDescuento: number) => {
    const line = lines.find((l) => l.key === key);
    if (!line) return;
    const check = evaluateLineDiscount({
      descuento: line.descuento,
      esRegalo: line.esRegalo,
      campaignPct: line.campaignPct,
      bracketPercents,
      monthlyCounts,
      limits,
    });
    if (!check.needsAuthorization) {
      if (line.motivo) updateLine(key, { motivo: '' });
      return;
    }
    // Already has a motivo AND descuento didn't change → nothing to do.
    if (line.motivo && previousDescuento === line.descuento) return;
    setAuthDialog({
      lineKey: key,
      articulo: line.articulo,
      descuento: line.descuento,
      motivo: line.motivo,
      previousDescuento,
      previousMotivo: line.motivo,
      suggested: check.motivo,
    });
  };

  const confirmAuthDialog = () => {
    if (!authDialog) return;
    updateLine(authDialog.lineKey, { motivo: authDialog.motivo.trim() });
    setAuthDialog(null);
  };

  const cancelAuthDialog = () => {
    if (!authDialog) return;
    updateLine(authDialog.lineKey, {
      descuento: authDialog.previousDescuento,
      motivo: authDialog.previousMotivo,
    });
    setAuthDialog(null);
  };

  // --- derived totals + authorization per line ---
  const linesWithComputed = useMemo(() => {
    // `extraUsed` tracks the A/B/C bracket draws consumed by earlier
    // lines in THIS cotización so the Nth same-bracket line gets flagged
    // correctly relative to the monthly allowance.
    const extraUsed = { A: 0, B: 0, C: 0 };
    return lines.map((l) => {
      const t = calcLineTotals(l);
      const check = evaluateLineDiscount({
        descuento: l.descuento,
        esRegalo: l.esRegalo,
        campaignPct: l.campaignPct,
        bracketPercents,
        monthlyCounts,
        limits,
        extraUsed,
      });
      if (check.category === 'A') extraUsed.A += 1;
      else if (check.category === 'B') extraUsed.B += 1;
      else if (check.category === 'C') extraUsed.C += 1;
      return { line: l, totals: t, check };
    });
  }, [lines, monthlyCounts, limits, bracketPercents]);

  const invoice = useMemo(() => calcInvoiceTotals(lines), [lines]);

  // Gift gating: a line can only be marked Regalo when (a) the cotización
  // Total (con IVA) reaches the configured minimum for this currency, AND
  // (b) the selected article's Cuenta Contable is Accesorios. `regaloMinimo
  // <= 0` means the amount gate is unconfigured → that part is open.
  const regaloMinimo = useRegaloMinimoThreshold(moneda);
  // Gift threshold compares against the SUBTOTAL (sin IVA) — the merchandise
  // value — not the IVA-inflated total, so the bar is consistent across IVA
  // rates (AR 21/10.5, MX 16/8).
  const montoOk = regaloMinimo <= 0 || invoice.subtotalDescontado >= regaloMinimo;
  const draftIsAccesorio = isArticuloRegalable(articuloDraft);
  // Monthly gift quota for this vendor, counted in UNITS (cantidad), not
  // lines: gifts already persisted this month plus the regalo units in the
  // current draft. `regaloLimit <= 0` = sin límite.
  const giftsUsedThisMonth =
    (regalosMesQ.data ?? 0) +
    lines.filter((l) => l.esRegalo).reduce((s, l) => s + l.cantidad, 0);
  // Block BEFORE adding: gifting the article in the toolbar would add
  // `cantidadDraftNum` units, so check that those units still fit under the
  // cap (used + about-to-add ≤ limit), not merely that we're under it now.
  const monthlyGiftOk =
    regaloLimit <= 0 ||
    giftsUsedThisMonth + Math.max(1, cantidadDraftNum) <= regaloLimit;
  const giftEnabled = montoOk && draftIsAccesorio && monthlyGiftOk;
  // Reason-specific hint shown on hover over the Regalo button (Radix
  // Tooltip) and used as its aria-label. Whichever gate is blocking wins;
  // when enabled it explains the armed/arm action.
  const giftsRemaining = Math.max(0, regaloLimit - giftsUsedThisMonth);
  const giftHint = !montoOk
    ? `Para regalar, el subtotal (sin IVA) debe ser ≥ ${formatMoney(regaloMinimo, moneda)} ${moneda}`
    : !draftIsAccesorio
      ? 'Solo se pueden regalar accesorios'
      : !monthlyGiftOk
        ? giftsRemaining <= 0
          ? `Alcanzaste el límite de regalos del mes (${regaloLimit})`
          : `Te ${giftsRemaining === 1 ? 'queda 1 regalo' : `quedan ${giftsRemaining} regalos`} este mes — reducí la cantidad`
        : giftArmed
          ? 'Próximo artículo será regalo'
          : 'Marcar próximo artículo como regalo';
  // Drop a stale armed state whenever gifting is no longer applicable
  // (total under the limit, non-accesorio selected, or monthly cap reached).
  useEffect(() => {
    if (!giftEnabled && giftArmed) setGiftArmed(false);
  }, [giftEnabled, giftArmed]);

  // Stock remaining for the article currently selected in the add-line
  // toolbar — this is the cotizable pool (Disponible + Por Ingresar)
  // minus quantities already committed to OTHER draft lines for the
  // same article. If the user keeps the toolbar open with the same
  // article selected after adding a line, the count drops by what
  // they just added.
  const draftArticuloStock = useMemo(() => {
    if (!articuloDraft) return null;
    const total = calcStockCotizable(inventarioByArticulo.get(articuloDraft.id));
    const used = usedByArticulo.get(articuloDraft.id) ?? 0;
    return Math.max(0, total - used);
  }, [articuloDraft, inventarioByArticulo, usedByArticulo]);

  const draftOverStock =
    draftArticuloStock !== null && cantidadDraftNum > draftArticuloStock;

  // Aggregate stock check: a single article spread across multiple
  // lines must not collectively exceed the cotizable total. Each
  // line's `stockDisponible` already holds the article's total, so
  // we group + sum in here rather than re-reading inventario.
  const anyOverStock = useMemo(() => {
    const totalsByArticulo = new Map<string, number>();
    for (const l of lines) {
      // Earliest-seen line for an article holds the canonical total
      // (they should all match, but we take the first to be safe).
      if (!totalsByArticulo.has(l.articuloId)) {
        totalsByArticulo.set(l.articuloId, l.stockDisponible);
      }
    }
    for (const [aid, total] of totalsByArticulo) {
      const sum = usedByArticulo.get(aid) ?? 0;
      if (sum > total) return true;
    }
    return false;
  }, [lines, usedByArticulo]);
  // Celeste = la línea depende de stock por ingresar (rojo sigue siendo el
  // over-stock de arriba, que manda).
  const estadoPorLinea = useMemo(
    () => estadosPorLinea(lines, stockDe),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, inventarioByArticulo],
  );
  const anyAuthMissingMotivo = linesWithComputed.some(
    ({ line, check }) => check.needsAuthorization && !line.motivo.trim(),
  );
  // A row-level cantidad of 0 is allowed in the input (so users can
  // backspace to clear the field — the 0 renders red as a visible
  // "fix this" cue). It's NOT allowed at submit time though.
  const anyEmptyCantidad = lines.some((l) => l.cantidad < 1);
  const canSubmit = Boolean(
    contact &&
      lines.length > 0 &&
      !anyOverStock &&
      !anyAuthMissingMotivo &&
      !anyEmptyCantidad &&
      (montoOk || !lines.some((l) => l.esRegalo)) &&
      profile?.usuario_app,
  );

  // --- submit ---
  const mutation = useMutation({
    mutationFn: async () => {
      if (!contact) throw new Error('No contact selected');
      const payloadLines: VentaLinePayload[] = linesWithComputed.map(({ line, totals, check }) => ({
        articuloId: line.articuloId,
        articulo: line.articulo,
        monto: line.monto,
        cantidad: line.cantidad,
        iva: ivaAEnviar(line.iva, exterior, activeCountry.ivaRateDefault),
        // ^ Legacy IVA_RO column expects a non-zero rate per country
        // (AR: 21 / 10,5; MX: 16 / 8). 0-IVA lines still render as 0
        // on the invoice (ivaAmount=0); we write the country default
        // server-side so the column constraint is satisfied. Cliente del
        // exterior: va 0 (exportación sin IVA).
        descuento: line.descuento,
        esRegalo: line.esRegalo,
        llevaGarantia: line.llevaGarantia,
        garantiaVence: line.garantiaVence,
        // Sólo si la línea sigue dependiendo de stock por ingresar; ausente = no se guarda.
        fechaEstimadaIngreso:
          estadoPorLinea.get(line.key) === 'por-ingresar' ? line.fechaEstimadaIngreso ?? undefined : undefined,
        subtotal: totals.subtotal,
        subtotalDescontado: totals.subtotalDescontado,
        total: totals.total,
        needsAuthorization: check.needsAuthorization,
        tipoDescuentoLabel: check.tipoLabel,
        motivoAutorizacion: line.motivo.trim() || check.motivo,
        campaignPct: line.campaignPct,
      }));
      return createVenta({
        header: {
          clienteId: contact.id,
          cliente: contact.fields.NombreApellido_C?.trim() || '—',
          correo: contact.fields.Mail_C ?? null,
          referido: contact.fields.Referido_C ?? null,
          moneda,
          subtotal: invoice.subtotal,
          iva21: invoice.iva21,
          iva105: invoice.iva105,
          total: invoice.total,
        },
        lines: payloadLines,
      });
    },
    onSuccess: () => {
      setConfirmOpen(false);
      // Cotización may have emitted `AutorizacionDescuentos` rows when any
      // line needed authorization — refresh the sidebar badge.
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
      // Refresh the monthly gift count if this cotización gifted anything.
      qc.invalidateQueries({ queryKey: ['nueva-cotizacion', countryCode, 'regalos-mes'] });
      setStatus({ kind: 'success', message: 'Cotización creada correctamente.' });
    },
    onError: (err) => {
      setConfirmOpen(false);
      // Keep the technical detail in the dev console; the backend already
      // logs the full Graph error body so this is the second breadcrumb
      // for debugging without exposing it to end users.
      // eslint-disable-next-line no-console
      console.error('[ventas] createVenta failed', err);
      // An admin may have changed a discount/regalo/campaña rule while the
      // seller had this open — the server rejects the stale claim. Refetch
      // the reference data so the form re-evaluates and the seller can fix
      // the flagged line and resubmit, instead of a dead-end "try later".
      const stale = staleConfigError(err);
      if (stale) {
        qc.invalidateQueries({ queryKey: ['nueva-cotizacion', 'parametros'] });
        qc.invalidateQueries({ queryKey: ['nueva-cotizacion', countryCode, 'descuentos-mes'] });
        qc.invalidateQueries({ queryKey: ['nueva-cotizacion', countryCode, 'regalos-mes'] });
        qc.invalidateQueries({ queryKey: ['nueva-cotizacion', countryCode, 'campanias'] });
        setStatus({ kind: 'error', message: staleConfigMessage(stale) });
        return;
      }
      setStatus({
        kind: 'error',
        message: 'No se pudo crear la cotización. Intentá nuevamente en unos minutos.',
      });
    },
  });

  const onSuccessClose = () => {
    setStatus(null);
    navigate('/ventas');
  };

  // Keep article picker's cantidad default to 1 whenever the article resets.
  useEffect(() => {
    if (!articuloDraft) setCantidadDraft('1');
  }, [articuloDraft]);

  return (
    <div className="flex h-full flex-col gap-4">
      {/* TOP TOOLBAR */}
      <div className="flex flex-nowrap items-center gap-3">
        <Button
          size="icon"
          className="h-10 w-10 shrink-0 rounded-full bg-foreground text-background hover:bg-foreground/90"
          onClick={() => navigate('/ventas')}
          aria-label="Volver a Ventas"
          title="Volver a Ventas"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Cotizaciones{' '}
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            Fecha: {todayAr()} · Moneda: {moneda}
          </span>
        </h1>
        <div className="ml-auto w-80 shrink-0">
          <Combobox
            value={contact}
            onChange={setContact}
            items={sortedContactos}
            getKey={(c) => c.id}
            getLabel={(c) =>
              c.fields.NombreApellido_C?.trim() ||
              c.fields.RazonSocial_C?.trim() ||
              '—'
            }
            getSublabel={(c) => c.fields.Mail_C}
            matcher={(c, q) =>
              (c.fields.NombreApellido_C ?? '').toLowerCase().includes(q) ||
              (c.fields.RazonSocial_C ?? '').toLowerCase().includes(q) ||
              (c.fields.Mail_C ?? '').toLowerCase().includes(q) ||
              (c.fields.CUIT_C ?? '').toLowerCase().includes(q)
            }
            placeholder="Seleccionar contacto"
            // Match the toolbar's h-12 buttons / inputs so the row reads
            // as a single band — Combobox defaults to h-10.
            className="h-12"
          />
        </div>
      </div>

      {/* CONTACT INFO + ARTICLE PICKER */}
      <div className="flex flex-wrap items-center gap-4 rounded-2xl border bg-card p-4">
        <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-5">
          <InfoField label="Nombre" value={contact?.fields.NombreApellido_C} />
          <InfoField label="Mail" value={contact?.fields.Mail_C} />
          <InfoField label="CUIT" value={contact?.fields.CUIT_C} />
          <InfoField label="Teléfono" value={contact?.fields.Celular_C} />
          <InfoField label="Referido" value={contact?.fields.Referido_C} />
        </div>
        <div className="flex shrink-0 items-end gap-2">
          <div className="w-64">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Artículo
            </label>
            <Combobox
              value={articuloDraft}
              onChange={(a) => {
                setArticuloDraft(a);
                // Picking an article always reseeds Cantidad to 1 — the
                // expected default for an add-to-cart row. Without this,
                // a Cantidad that had been backspaced down to 0 (onBlur
                // fills empty → '0', red) carries over to the new
                // article and the user has to manually retype.
                setCantidadDraft('1');
                // Warn up-front if even the default of 1 exceeds the
                // newly-selected article's stock pool (Disponible +
                // PorIngresar minus quantities already committed to
                // other lines) — i.e. the article has no stock at all.
                if (a) {
                  const total = calcStockCotizable(inventarioByArticulo.get(a.id));
                  const used = usedByArticulo.get(a.id) ?? 0;
                  const remaining = Math.max(0, total - used);
                  if (remaining < 1) {
                    toast.error('Stock insuficiente', {
                      description: `${
                        a.fields.Articulo_ART?.trim() || 'Artículo'
                      } — disponible: ${remaining} Un.`,
                    });
                  }
                }
              }}
              items={sortedArticulos}
              getKey={(a) => a.id}
              getLabel={(a) => a.fields.Articulo_ART?.trim() || a.fields.Concat_ART || '—'}
              getSublabel={(a) => {
                // QA #3: show NET price (no IVA tag) plus stock count.
                // Out-of-stock rows still show "Sin stock" so admins can
                // see the article exists but is unavailable today.
                // Campaign info lives in the right-aligned badge (renderItemRight)
                // so it never gets truncated by the sublabel ellipsis.
                const price = `${formatMoney(articuloPrice(a, moneda), moneda)} ${moneda}`;
                const stock = articleStockById.get(a.id) ?? 0;
                const { disponible, porIngresar } = stockDe(a.id);
                const used = usedByArticulo.get(a.id) ?? 0;
                if (estadoSiguienteUnidad(disponible, porIngresar, used) === 'por-ingresar') {
                  const dispRest = Math.max(0, disponible - used);
                  return `${price} · Disp: ${dispRest} · Por ingresar: ${Math.max(0, stock - dispRest)}`;
                }
                const stockLabel = stock > 0 ? `Stock: ${stock}` : 'Sin stock';
                return `${price} · ${stockLabel}`;
              }}
              renderItemRight={(a) => {
                const campaign = campaignByArticulo.get(a.id);
                if (!campaign) return null;
                return (
                  <span
                    className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700"
                    title={`Campaña activa: ${campaign.pct}% off`}
                  >
                    <BadgePercent className="h-3 w-3" aria-hidden />
                    {campaign.pct}%
                  </span>
                );
              }}
              getItemClassName={(a) => {
                if ((articleStockById.get(a.id) ?? 0) <= 0) return CLASE_ITEM_SIN_STOCK;
                const { disponible, porIngresar } = stockDe(a.id);
                const used = usedByArticulo.get(a.id) ?? 0;
                return estadoSiguienteUnidad(disponible, porIngresar, used) === 'por-ingresar'
                  ? CLASE_ITEM_POR_INGRESAR
                  : undefined;
              }}
              matcher={(a, q) =>
                (a.fields.Articulo_ART ?? '').toLowerCase().includes(q) ||
                (a.fields.Codigo_ART ?? '').toLowerCase().includes(q) ||
                (a.fields.Concat_ART ?? '').toLowerCase().includes(q)
              }
              placeholder="Seleccionar artículo"
            />
          </div>
          <div className="w-24">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Cantidad
            </label>
            <Input
              // text+inputMode for the same DOM-sync reason as the
              // row-level cantidad input below — type="number" can
              // leave a stale "01" on screen when the typed string
              // and the bound state agree as numbers but differ as
              // strings.
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              variant="filled"
              aria-invalid={draftOverStock || cantidadDraftNum < 1}
              title={
                draftOverStock && draftArticuloStock !== null
                  ? `Stock disponible: ${draftArticuloStock} Un.`
                  : undefined
              }
              className={cn(
                'h-10',
                // Red highlight whenever the value is invalid: stock
                // exceeded, OR cantidad fell below 1 (empty / 0). This
                // is the user-visible "you need to fix this" cue —
                // paired with the disabled Add button below.
                (draftOverStock || cantidadDraftNum < 1) &&
                  'border border-destructive bg-destructive/10 text-destructive focus-visible:border-destructive',
              )}
              value={cantidadDraft}
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => {
                // Strip non-digits + leading zeros so "01" → "1" and
                // empty stays empty (so the field can be cleared with
                // backspace; onBlur fills with "0" + red).
                const digits = e.target.value.replace(/\D/g, '');
                const raw = digits.replace(/^0+(?=\d)/, '');
                setCantidadDraft(raw);
                const parsed = parseInt(raw, 10);
                if (!Number.isFinite(parsed)) return;
                const wasOver = draftOverStock;
                const willOver =
                  draftArticuloStock !== null && parsed > draftArticuloStock;
                if (willOver && !wasOver && articuloDraft) {
                  toast.error('Stock insuficiente', {
                    description: `${
                      articuloDraft.fields.Articulo_ART?.trim() || 'Artículo'
                    } — disponible: ${draftArticuloStock} Un.`,
                  });
                }
              }}
              onBlur={() => {
                // If the user backspaced everything out, fill with
                // "0" so the invalid state is visually concrete (red
                // highlight + literal 0) rather than an empty field
                // that could read as "didn't get to it yet". User
                // can't proceed: Add button stays disabled until the
                // value is ≥ 1.
                if (cantidadDraft.trim() === '') setCantidadDraft('0');
              }}
            />
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* span wrapper: a disabled button has pointer-events-none,
                  so hover passes through to this span and the tooltip
                  still fires when the gift is gated. */}
              <span className="inline-flex">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={!giftEnabled}
                  aria-label={giftHint}
                  onClick={() => setGiftArmed((v) => !v)}
                  className={cn(
                    'h-10 w-10 disabled:cursor-not-allowed disabled:opacity-50',
                    giftArmed &&
                      'border-foreground bg-foreground text-background hover:bg-foreground/90 hover:text-background',
                  )}
                >
                  <Gift className="h-4 w-4" aria-hidden />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{giftHint}</TooltipContent>
          </Tooltip>
          <GarantiaToggle armed={garantiaArmed} onToggle={() => setGarantiaArmed((v) => !v)} />
          <Button
            type="button"
            size="icon"
            className="h-10 w-10 bg-foreground text-background hover:bg-foreground/90"
            onClick={() => addLine()}
            disabled={!articuloDraft || cantidadDraftNum < 1 || draftOverStock}
            aria-label="Agregar artículo"
            title={
              draftOverStock
                ? 'Cantidad supera el stock disponible'
                : 'Agregar artículo'
            }
          >
            <Plus className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>

      {/* Gift-gating reasons surface on hover over the Regalo button
          (its `title`), so no inline note here — keeps the toolbar
          row clean. */}

      {/* LINES TABLE or EMPTY STATE */}
      <div className="flex min-h-0 flex-1 flex-col">
        {lines.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border bg-card">
            <div className="min-h-0 flex-1 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Artículo</th>
                    <th className="w-32 px-4 py-3 text-right font-semibold">Monto</th>
                    <th className="w-24 px-4 py-3 font-semibold">Cantidad</th>
                    <th className="w-32 px-4 py-3 text-right font-semibold">Subtotal</th>
                    <th className="w-16 px-4 py-3 font-semibold">IVA</th>
                    <th className="w-24 px-4 py-3 font-semibold">Descuento</th>
                    <th className="w-32 px-4 py-3 text-right font-semibold">Total</th>
                    <th className="w-20 px-4 py-3 text-right font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {linesWithComputed.map(({ line, totals, check }) => {
                    // Over-stock is now aggregated per article: a line
                    // is flagged when the sum of all draft lines for
                    // its articulo exceeds the cotizable total
                    // (Disponible + Por Ingresar). Two lines of the
                    // same article each at half the stock are fine;
                    // either one bumped past the limit flags both.
                    const articleSum = usedByArticulo.get(line.articuloId) ?? 0;
                    const overStock = articleSum > line.stockDisponible;
                    const porIngresar = !overStock && estadoPorLinea.get(line.key) === 'por-ingresar';
                    return (
                      <tr
                        key={line.key}
                        className={overStock ? 'bg-rose-50/60' : porIngresar ? 'bg-sky-50/60' : undefined}
                      >
                        <td className="px-4 py-3 align-middle">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">{line.articulo}</span>
                            {line.esRegalo && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-700">
                                <Gift className="h-3 w-3" aria-hidden /> Regalo
                              </span>
                            )}
                            <GarantiaBadge
                              linea={line}
                              onQuitar={() => updateLine(line.key, SIN_GARANTIA)}
                            />
                            {line.campaignPct != null && !line.esRegalo && (
                              <span
                                className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700"
                                title={`Campaña activa: ${line.campaignPct}% off`}
                              >
                                <BadgePercent className="h-3 w-3" aria-hidden /> Campaña {line.campaignPct}%
                              </span>
                            )}
                            {check.needsAuthorization && (
                              <span
                                className="inline-flex items-center gap-1 rounded-md bg-fuchsia-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-fuchsia-700"
                                title={line.motivo.trim() || 'Motivo pendiente'}
                              >
                                Aprobación Requerida
                              </span>
                            )}
                            {porIngresar && (
                              <>
                                <PorIngresarBadge />
                                <FechaIngresoPicker
                                  opciones={fechasPorIngresarQ.data?.[line.articuloId]}
                                  value={line.fechaEstimadaIngreso}
                                  onChange={(f) => updateLine(line.key, { fechaEstimadaIngreso: f })}
                                />
                              </>
                            )}
                            {overStock && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-rose-700">
                                <AlertTriangle className="h-3 w-3" aria-hidden /> Stock: {line.stockDisponible} Un.
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right align-middle">
                          <MoneyCell amount={line.monto} moneda={moneda} />
                        </td>
                        <td className="px-4 py-3 align-middle">
                          {line.llevaNroSerie ? (
                            // Serialized article: cantidad is locked to 1 —
                            // each unit is its own line (own NroSerie). To
                            // sell more, add the article again (auto-splits).
                            <Input
                              type="text"
                              variant="filled"
                              disabled
                              readOnly
                              className="h-9 w-20 cursor-not-allowed opacity-70"
                              title="Artículo serializado: 1 unidad por línea. Agregá el artículo de nuevo para sumar unidades."
                              value="1"
                            />
                          ) : (
                            <Input
                              // text+inputMode="numeric" instead of
                              // type="number" so React's controlled-
                              // value reconciliation always syncs the
                              // DOM exactly (with type="number" the
                              // browser keeps the user-typed "01" when
                              // the bound number rounds to 1, leaving
                              // a stale leading zero on screen). Mobile
                              // still gets the numeric keypad via
                              // inputMode + pattern.
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              variant="filled"
                              className={cn(
                                'h-9 w-20',
                                line.cantidad < 1 &&
                                  'border border-destructive bg-destructive/10 text-destructive focus-visible:border-destructive',
                              )}
                              aria-invalid={line.cantidad < 1}
                              value={String(line.cantidad)}
                              onFocus={(e) => e.currentTarget.select()}
                              onChange={(e) => {
                                // Strip non-digits + collapse leading
                                // zeros so "01" → "1", but keep the
                                // explicit "0" empty-state.
                                const digits = e.target.value.replace(/\D/g, '');
                                const cleaned =
                                  digits === ''
                                    ? '0'
                                    : digits.replace(/^0+(?=\d)/, '');
                                updateLine(line.key, {
                                  cantidad: Math.max(0, parseInt(cleaned, 10) || 0),
                                });
                              }}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 text-right align-middle">
                          <MoneyCell amount={totals.subtotal} moneda={moneda} />
                        </td>
                        <td className="px-4 py-3 align-middle text-muted-foreground">
                          {line.iva === 0
                            ? '—'
                            : `${String(line.iva).replace('.', ',')}%`}
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <DescuentoCell
                            descuento={line.descuento}
                            esRegalo={line.esRegalo}
                            onChange={(next) => updateLine(line.key, { descuento: next })}
                            onCommit={(previous) => handleDescuentoCommit(line.key, previous)}
                          />
                        </td>
                        <td className="px-4 py-3 text-right align-middle">
                          <MoneyCell amount={totals.total} moneda={moneda} emphasise />
                        </td>
                        <td className="px-4 py-3 text-right align-middle">
                          <button
                            type="button"
                            onClick={() => removeLine(line.key)}
                            aria-label="Eliminar"
                            title="Eliminar"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* BOTTOM TOTALS + SUBMIT */}
      <div className="flex shrink-0 flex-wrap items-center gap-3 rounded-2xl border bg-card p-4">
        {exterior && (
          <p className="w-full text-xs font-medium text-muted-foreground">{NOTA_EXPORTACION_SIN_IVA}</p>
        )}
        <TotalCell label="Subtotal" value={invoice.subtotalDescontado} moneda={moneda} />
        {Object.entries(invoice.ivaByRate)
          .map(([k, v]) => [Number(k), v] as const)
          .sort((a, b) => b[0] - a[0])
          .map(([rate, amount]) => (
            <TotalCell
              key={rate}
              label={`IVA ${formatIvaRate(rate)}%`}
              value={amount}
              moneda={moneda}
            />
          ))}
        <TotalCell
          label="Total cotización"
          value={invoice.total}
          moneda={moneda}
          emphasise
        />
        <Button
          type="button"
          className="ml-auto h-11 gap-2 bg-foreground px-5 text-background hover:bg-foreground/90"
          disabled={!canSubmit || mutation.isPending}
          onClick={() => setConfirmOpen(true)}
        >
          <Check className="h-4 w-4" aria-hidden />
          Realizar cotización
        </Button>
      </div>

      <GarantiaFechaDialog
        articulo={garantiaPendiente}
        onCancel={() => setGarantiaPendiente(null)}
        onConfirm={(vence) => {
          setGarantiaPendiente(null);
          addLine(vence);
        }}
      />

      <SuccessDialog
        open={status?.kind === 'success'}
        message={status?.kind === 'success' ? status.message : ''}
        onClose={onSuccessClose}
      />
      <ErrorDialog
        open={status?.kind === 'error'}
        message={status?.kind === 'error' ? status.message : ''}
        onClose={() => setStatus(null)}
      />
      <DescuentoAprobacionDialog
        state={authDialog}
        onMotivoChange={(next) =>
          setAuthDialog((prev) => (prev ? { ...prev, motivo: next } : prev))
        }
        onConfirm={confirmAuthDialog}
        onCancel={cancelAuthDialog}
      />
      <ConfirmarCotizacionDialog
        open={confirmOpen && !mutation.isPending}
        cliente={contact?.fields.NombreApellido_C?.trim() || '—'}
        lineas={lines.length}
        unidades={lines.reduce((sum, l) => sum + (l.cantidad || 0), 0)}
        total={invoice.total}
        moneda={moneda}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => mutation.mutate()}
      />
      {mutation.isPending && <ProcesandoOverlay message="Procesando cotización…" />}
    </div>
  );
}

function TotalCell({
  label,
  value,
  moneda,
  emphasise,
}: {
  label: string;
  value: number;
  moneda: Moneda;
  emphasise?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex h-11 flex-col justify-center rounded-xl border bg-secondary px-4 leading-tight',
        emphasise && 'border-primary/30 bg-primary/5',
      )}
    >
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          'text-sm font-semibold',
          emphasise ? 'text-primary' : 'text-foreground',
        )}
      >
        {formatMoney(value, moneda)}{' '}
        <span className="text-[10px] font-medium text-muted-foreground">{moneda}</span>
      </span>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full min-h-0 flex-col items-center justify-center gap-3 rounded-2xl border bg-card p-10 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Plus className="h-6 w-6" aria-hidden />
      </div>
      <p className="text-lg font-semibold text-foreground">Sin artículos</p>
      <p className="max-w-md text-sm text-muted-foreground">
        Seleccioná un contacto y agregá un artículo para generar una cotización.
      </p>
    </div>
  );
}

function ConfirmarCotizacionDialog({
  open,
  cliente,
  lineas,
  unidades,
  total,
  moneda,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  cliente: string;
  lineas: number;
  unidades: number;
  total: number;
  moneda: Moneda;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Confirmar cotización</DialogTitle>
          <DialogDescription>
            Revisá que los datos estén en orden antes de generar la
            cotización. ¿Está seguro que desea continuar?
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <dt className="text-muted-foreground">Cliente</dt>
              <dd className="font-medium text-foreground">{cliente}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Total</dt>
              <dd className="font-semibold text-foreground">
                {formatMoney(total, moneda)}{' '}
                <span className="text-xs font-medium text-muted-foreground">{moneda}</span>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Artículos</dt>
              <dd className="font-medium text-foreground">{lineas}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Unidades totales</dt>
              <dd className="font-medium text-foreground">{unidades}</dd>
            </div>
          </dl>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            className="bg-foreground text-background hover:bg-foreground/90"
          >
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

